/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React from "react";
import { Youtube, Heart, Sparkles, BookOpen, Scroll, ShieldCheck, Send } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";

interface RoyalFooterProps {
  onNavigate?: (view: "home" | "videos" | "articles" | "quiz" | "haftkhan") => void;
}

export default function RoyalFooter({ onNavigate }: RoyalFooterProps = {}) {
  const { isRtl, t, lang } = useLanguage();
  const currentYear = new Date().getFullYear();

  return (
    <footer className="relative bg-[#0a0a0a] border-t border-amber-500/20 text-gray-400 py-16 px-4 overflow-hidden">
      {/* Background radial gold glow */}
      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-96 h-96 bg-amber-500/5 blur-[120px] rounded-full pointer-events-none" />

      <div className="max-w-6xl mx-auto relative z-10 flex flex-col items-center">
        {/* Calligraphic/Premium Brand Divider */}
        <div className="flex items-center gap-4 mb-8 w-full justify-center select-none">
          <div className="h-[1px] flex-1 bg-gradient-to-r from-transparent to-amber-500/30" />
          <div className="flex items-center gap-1.5 text-amber-400">
            <Sparkles className="w-4 h-4 animate-pulse text-amber-400" />
            <span className="text-xs tracking-widest font-mono uppercase text-amber-500">{t("brandSubtitle")}</span>
            <Sparkles className="w-4 h-4 animate-pulse text-amber-400" />
          </div>
          <div className="h-[1px] flex-1 bg-gradient-to-l from-transparent to-amber-500/30" />
        </div>

        {/* The Channel Signature Sign-off (User explicit request) */}
        <div className="text-center max-w-2xl px-4 select-none mb-10">
          <h3 className="text-xl md:text-2xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-amber-200 via-amber-400 to-amber-200 font-sans tracking-wide leading-relaxed filter drop-shadow-[0_2px_4px_rgba(0,0,0,0.5)]">
            {t("footerSignature")}
          </h3>
          <p className="text-xs text-amber-400 font-sans mt-2 tracking-widest uppercase font-medium">
            {t("footerSubtitle")}
          </p>
        </div>

        {/* Footer Navigation Columns (SEO friendly & premium) */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-8 w-full border-b border-amber-500/5 pb-10 mb-10 text-start" dir={isRtl ? "rtl" : "ltr"}>
          <div>
            <h4 className="text-xs font-bold text-amber-400 uppercase tracking-wider mb-4 font-sans">{t("footerSectionTitle")}</h4>
            <ul className="space-y-2 text-xs">
              <li>
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    if (onNavigate) onNavigate("home");
                    else window.scrollTo({ top: 0, behavior: "smooth" });
                  }}
                  className="hover:text-amber-300 transition-colors font-sans bg-transparent border-none cursor-pointer p-0 text-start w-full block text-gray-400"
                >
                  {t("footerSectionHome")}
                </button>
              </li>
              <li>
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    if (onNavigate) onNavigate("videos");
                  }}
                  className="hover:text-amber-300 transition-colors font-sans bg-transparent border-none cursor-pointer p-0 text-start w-full block text-gray-400 mt-2"
                >
                  {t("footerSectionVideos")}
                </button>
              </li>
              <li>
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    if (onNavigate) onNavigate("articles");
                  }}
                  className="hover:text-amber-300 transition-colors font-sans bg-transparent border-none cursor-pointer p-0 text-start w-full block text-gray-400 mt-2"
                >
                  {t("footerSectionArticles")}
                </button>
              </li>
              <li>
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    if (onNavigate) onNavigate("quiz");
                  }}
                  className="hover:text-amber-300 transition-colors font-sans bg-transparent border-none cursor-pointer p-0 text-start w-full block text-gray-400 mt-2"
                >
                  {t("navQuiz")}
                </button>
              </li>
              <li>
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    if (onNavigate) onNavigate("haftkhan");
                  }}
                  className="hover:text-amber-300 transition-colors font-sans bg-transparent border-none cursor-pointer p-0 text-start w-full block text-gray-400 mt-2"
                >
                  {t("navHaftKhan")}
                </button>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="text-xs font-bold text-amber-400 uppercase tracking-wider mb-4 font-sans">{t("footerArchetypesTitle")}</h4>
            <ul className="space-y-2 text-xs">
              <li>
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    if (onNavigate) onNavigate("articles");
                  }}
                  className="hover:text-amber-300 transition-colors font-sans bg-transparent border-none cursor-pointer p-0 text-start w-full block text-gray-400"
                >
                  {t("footerArchSelf")}
                </button>
              </li>
              <li>
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    if (onNavigate) onNavigate("articles");
                  }}
                  className="hover:text-amber-300 transition-colors font-sans bg-transparent border-none cursor-pointer p-0 text-start w-full block text-gray-400 mt-2"
                >
                  {t("footerArchShadow")}
                </button>
              </li>
              <li>
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    if (onNavigate) onNavigate("articles");
                  }}
                  className="hover:text-amber-300 transition-colors font-sans bg-transparent border-none cursor-pointer p-0 text-start w-full block text-gray-400 mt-2"
                >
                  {t("footerArchWise")}
                </button>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="text-xs font-bold text-amber-400 uppercase tracking-wider mb-4 font-sans">{t("footerSocialTitle")}</h4>
            <ul className="space-y-2 text-xs">
              <li>
                <a
                  href="https://www.youtube.com/@razoravan"
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-1.5 justify-start hover:text-red-400 transition-colors"
                >
                  <Youtube className="w-4 h-4 text-red-500" />
                  <span className="font-sans">{t("footerYoutube")}</span>
                </a>
              </li>
              <li>
                <a
                  href="https://t.me/razoravan"
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-1.5 justify-start hover:text-blue-400 transition-colors"
                >
                  <Send className="w-4 h-4 text-blue-400" />
                  <span className="font-sans">{t("footerTelegram")}</span>
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Gnostic Quote (User requested) */}
        <div className="text-center mb-8 max-w-lg select-none" dir={isRtl ? "rtl" : "ltr"}>
          <p className="text-sm md:text-base text-amber-400 italic font-sans leading-relaxed text-center font-medium tracking-wide">
            {isRtl ? (
              <>
                «اسطوره‌ها افسانه نیستند؛
                <br />
                حافظهٔ زندهٔ روان انسان‌اند.»
              </>
            ) : (
              <>
                "Myths are not legends;
                <br />
                They are the living memory of the human soul."
              </>
            )}
          </p>
        </div>

        {/* Lower Legal Line (Minimal & Clean) */}
        <div className="flex flex-col md:flex-row justify-center items-center w-full gap-4 text-[10px] text-gray-400 font-mono" dir={isRtl ? "rtl" : "ltr"}>
          <div>
            <span>{t("footerCopyright")}</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
